import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Plus, Trash2 } from 'lucide-react';

export default function AdminCampaigns() {
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);
  const [formData, setFormData] = useState<any>({
    type: 0,
    discountType: 0
  });

  const { data: campaigns = [], isLoading } = useQuery({
    queryKey: ['campaigns'],
    queryFn: async () => {
      const token = localStorage.getItem('access_token');
      const { data } = await axios.get('http://localhost:5000/api/campaigns', {
        headers: { Authorization: `Bearer ${token}` }
      });
      return data;
    }
  });

  const createMutation = useMutation({
    mutationFn: async (campaign: any) => {
      const token = localStorage.getItem('access_token');
      await axios.post('http://localhost:5000/api/campaigns', campaign, {
        headers: { Authorization: `Bearer ${token}` }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      setIsAdding(false);
      setFormData({ type: 0, discountType: 0 });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const token = localStorage.getItem('access_token');
      await axios.delete(`http://localhost:5000/api/campaigns/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['campaigns'] })
  });

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate({
      ...formData,
      type: Number(formData.type),
      discountType: Number(formData.discountType),
      discountValue: Number(formData.discountValue),
      minCartAmount: formData.minCartAmount ? Number(formData.minCartAmount) : null,
      isActive: true
    });
  };

  if (isLoading) return <div className="p-8"><div className="skeleton h-64 w-full"></div></div>;

  return (
    <div className="p-6 animate-fade-in">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold">Kampanya Yönetimi</h1>
          <p className="text-muted mt-2">Sepette veya ürünlerde otomatik uygulanacak kampanyalar ekleyin.</p>
        </div>
        <button onClick={() => setIsAdding(true)} className="btn btn-primary">
          <Plus size={20} /> Yeni Kampanya
        </button>
      </div>

      {isAdding && (
        <div className="card mb-8">
          <h3 className="font-bold mb-4">Yeni Kampanya Ekle</h3>
          <form onSubmit={handleSave} className="grid-2 gap-4">
            <div className="form-group col-span-2">
              <label className="form-label">Kampanya Adı</label>
              <input required type="text" className="form-input" placeholder="Örn: 200 TL Üzerine 30 TL İndirim" 
                value={formData.name || ''} onChange={e => setFormData({...formData, name: e.target.value})} />
            </div>
            
            <div className="form-group">
              <label className="form-label">Kampanya Tipi</label>
              <select className="form-input" value={formData.type} onChange={e => setFormData({...formData, type: e.target.value})}>
                <option value="0">Sepet İndirimi</option>
                <option value="1">Ürün / Kategori İndirimi</option>
                <option value="2">Ücretsiz Gönderim</option>
              </select>
            </div>
            
            <div className="form-group">
              <label className="form-label">İndirim Tipi</label>
              <select className="form-input" value={formData.discountType} onChange={e => setFormData({...formData, discountType: e.target.value})}>
                <option value="0">Yüzde (%)</option>
                <option value="1">Sabit Tutar (₺)</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">İndirim Değeri</label>
              <input required type="number" className="form-input" placeholder="30" 
                value={formData.discountValue || ''} onChange={e => setFormData({...formData, discountValue: e.target.value})} />
            </div>

            <div className="form-group">
              <label className="form-label">Min. Sepet Tutarı (₺)</label>
              <input type="number" className="form-input" placeholder="200" 
                value={formData.minCartAmount || ''} onChange={e => setFormData({...formData, minCartAmount: e.target.value})} />
            </div>

            <div className="form-group col-span-2">
              <label className="form-label">Açıklama</label>
              <textarea className="form-input" placeholder="Kampanya hakkında kısa açıklama..." rows={2}
                value={formData.description || ''} onChange={e => setFormData({...formData, description: e.target.value})}></textarea>
            </div>
            
            <div className="col-span-2 flex gap-2 justify-end mt-4">
              <button type="button" onClick={() => setIsAdding(false)} className="btn btn-ghost">İptal</button>
              <button type="submit" className="btn btn-primary" disabled={createMutation.isPending}>Kaydet</button>
            </div>
          </form>
        </div>
      )}

      <div className="grid-2 gap-6">
        {campaigns.map((c: any) => (
          <div key={c.id} className="card relative overflow-hidden group">
            <div className="absolute top-0 left-0 w-1 h-full bg-secondary"></div>
            
            <div className="flex justify-between items-start mb-2">
              <h3 className="text-xl font-bold">{c.name}</h3>
              <span className={`badge ${c.isActive ? 'badge-success' : 'badge-neutral'}`}>
                {c.isActive ? 'Aktif' : 'Pasif'}
              </span>
            </div>
            
            {c.description && <p className="text-muted text-sm mb-6">{c.description}</p>}
            
            <div className="grid grid-cols-2 gap-4 bg-surface-2 p-4 rounded-lg mb-6">
              <div>
                <p className="text-xs text-muted font-semibold uppercase tracking-wider">İndirim</p>
                <p className="font-bold text-lg">{c.discountType === 0 ? `%${c.discountValue}` : `${c.discountValue} ₺`}</p>
              </div>
              <div>
                <p className="text-xs text-muted font-semibold uppercase tracking-wider">Koşul</p>
                <p className="font-medium text-sm mt-1">
                  {c.minCartAmount ? `Min. ${c.minCartAmount} ₺ Sepet` : 'Koşulsuz'}
                </p>
              </div>
            </div>
            
            <div className="flex justify-end pt-4 border-t border-border">
              <button 
                onClick={() => { if(confirm('Silmek istediğinize emin misiniz?')) deleteMutation.mutate(c.id); }} 
                className="btn btn-sm btn-ghost text-error"
              >
                <Trash2 size={16} /> Kampanyayı Sonlandır
              </button>
            </div>
          </div>
        ))}
        {campaigns.length === 0 && !isAdding && (
          <div className="col-span-2 p-12 text-center text-muted card border-dashed">
            Henüz kampanya oluşturulmamış. Yeni bir kampanya ekleyerek satışları artırın.
          </div>
        )}
      </div>
    </div>
  );
}
